import React from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import css from './index.module.scss';
import { Button, Divider, IconNode, ToolTip, Typography } from '@components/base';
import Images from '@assets/images';
import QuoteFreightPort from '../../../quote-freight-port';
import QuoteIncoTerm from '../../../quote-inco-term';
import { formatPortOptions, getCommaSeperatedCurrencyAmount } from '@helpers/utils';
import { TextField } from '@components/common';
import { CONSOLIDATION_PRODUCT_DEFAULT_VALUE } from '../../../helper';
import QuoteProductFields from '../consolidation-product-fields';
import QuotePackageWrapper from '../../../quote-package-wrapper';

const ConsolidationQuote = (props: any) => {
  const {
    isEnquiry = false,
    quoteIndex,
    handleQuoteDeleteClick,
    handleDublicateQuote,
    quoteIsExpanded,
    totalWeight,
    totalWeightUnit,
    totalLocalFreight,
    totalLocalFreightCurrency,
    totalPortAndClearanceExpenses,
    totalPortAndClearanceExpensesCurrency,
    totalFreight,
    totalFreightCurrency,
    switchBlCost,
    switchBlCostCurrency,
    portOfLoading,
    portOfDischarge,
    insuranceCost,
    discount,
    financeCost,
    incoTerms,
    incoTermsLocation,
    filterState,
    selectedWeightSystemChildOptions,
    selectedCurrency,
    filterProductOptions,
    productOptions,
    filterPorts,
    portOfLoadingOptions,
    portOfDischargeOptions,
    primaryUnitWatch,
    currency1Watch,
    fxRateWatch,
    fieldLength,
    product
  } = props;
  const { control, setValue, getValues, resetField } = useFormContext();

  const {
    fields: productFields,
    append: productAppend,
    remove: productRemove
  } = useFieldArray({
    name: product,
    control
  });

  const handleAddProductFields = () => {
    productAppend(CONSOLIDATION_PRODUCT_DEFAULT_VALUE);
  };

  const handleDuplicateProductField = (quoteIndex: number, index: number) => {
    const values = getValues();
    const product = values.quote[quoteIndex].product[index];
    productAppend(product);
  };

  const { fcl, mtt, consolidation } = filterState;

  // Finance cost is set once per consolidation; its amount is the sum over the products
  const productsWatch = useWatch({ control, name: product });
  const totalFinanceCost = (productsWatch ?? []).reduce(
    (sum: number, item: any) => sum + Number(item?.cmValues?.financeCost || 0),
    0
  );

  const quoteIsExpandedWatch = useWatch({
    control,
    name: quoteIsExpanded
  });

  const totalWeightWatch = useWatch({
    control,
    name: totalWeight
  });

  const totalWeightUnitWatch = useWatch({
    control,
    name: totalWeightUnit
  });

  const totalLocalFreightWatch = useWatch({
    control,
    name: totalLocalFreight
  });

  const totalLocalFreightCurrencyWatch = useWatch({
    control,
    name: totalLocalFreightCurrency
  });

  const totalPortAndClearanceExpensesWatch = useWatch({
    control,
    name: totalPortAndClearanceExpenses
  });

  const totalPortAndClearanceExpensesCurrencyWatch = useWatch({
    control,
    name: totalPortAndClearanceExpensesCurrency
  });

  const totalFreightWatch = useWatch({
    control,
    name: totalFreight
  });

  const totalFreightCurrencyWatch = useWatch({
    control,
    name: totalFreightCurrency
  });

  const switchBlCostWatch = useWatch({
    control,
    name: switchBlCost
  });

  const switchBlCostCurrencyWatch = useWatch({
    control,
    name: switchBlCostCurrency
  });

  const portOfLoadingWatch = useWatch({
    control,
    name: portOfLoading
  });

  const portOfDischargeWatch = useWatch({
    control,
    name: portOfDischarge
  });

  const insuranceCostWatch = useWatch({
    control,
    name: insuranceCost
  });

  const discountWatch = useWatch({
    control,
    name: discount
  });

  const financeCostWatch = useWatch({
    control,
    name: financeCost
  });

  const incoTermsWatch = useWatch({
    control,
    name: incoTerms
  });

  const incoTermsLocationWatch = useWatch({
    control,
    name: incoTermsLocation
  });

  const toggleQuoteExpand = () => {
    setValue(quoteIsExpanded, !quoteIsExpandedWatch);
  };

  const isEdit = !handleDublicateQuote;

  return (
    <main className={css.mainWrapper}>
      {isEnquiry && (
        <div className={quoteIsExpandedWatch ? css.headerWrapper : css.headerWrapperCollapsed}>
          <div className={css.headerText} onClick={() => toggleQuoteExpand()}>
            Quote {quoteIndex + 1}
          </div>
          <div className={css.headerIcons}>
            <Button variant="text" onClick={() => handleDublicateQuote(quoteIndex)}>
              Duplicate Quote
            </Button>
            <ToolTip title="Delete">
              <div>
                <IconNode
                  src={fieldLength > 1 ? Images.deleteRed : Images.delete}
                  onClick={() => (fieldLength > 1 ? handleQuoteDeleteClick() : undefined)}
                />
              </div>
            </ToolTip>
            <ToolTip title={quoteIsExpandedWatch ? 'Collapse' : 'Expand'}>
              <div>
                <IconNode
                  src={quoteIsExpandedWatch ? Images.arrowUpBig : Images.arrowDownBig}
                  onClick={() => toggleQuoteExpand()}
                />
              </div>
            </ToolTip>
          </div>
        </div>
      )}

      {quoteIsExpandedWatch && (
        <div className={css.contentWrapper}>
          {!mtt && (
            <QuoteFreightPort
              totalLocalFreight={totalLocalFreight}
              totalLocalFreightCurrency={totalLocalFreightCurrency}
              totalPortAndClearanceExpenses={totalPortAndClearanceExpenses}
              totalPortAndClearanceExpensesCurrency={totalPortAndClearanceExpensesCurrency}
              selectedCurrency={selectedCurrency}
            />
          )}
          <div className={css.rowWrapper}>
            <QuotePackageWrapper
              label={fcl ? 'Total weight in one container' : 'Total weight'}
              unitPlaceholder="Unit"
              inputPlaceholder="Total weight"
              input={totalWeight}
              unit={totalWeightUnit}
              control={control}
              unitOptions={selectedWeightSystemChildOptions}
              currency={false}
            />
            <QuotePackageWrapper
              label={fcl ? 'Total International freight per FCL ' : 'Total International freight'}
              unitPlaceholder="Currency"
              inputPlaceholder="Total International freight"
              input={totalFreight}
              unit={totalFreightCurrency}
              control={control}
              unitOptions={selectedCurrency}
              currency
            />
          </div>
          <QuoteIncoTerm
            incoTerms={incoTerms}
            incotermWatch={incoTermsWatch}
            portOfLoading={portOfLoading}
            portOfLoadingOptions={portOfLoadingOptions}
            portOfDischarge={portOfDischarge}
            portOfDischargeOptions={portOfDischargeOptions}
            incoTermsLocation={incoTermsLocation}
            filterPorts={filterPorts}
            formatPortOptions={formatPortOptions}
          />
          <div className={mtt ? css.rowWrapper : css.rowWrapperHalf}>
            <Controller
              name={insuranceCost}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={
                    <span>
                      Credit and Cargo insurance cost
                      <Typography variant="label"> ( % )</Typography>
                    </span>
                  }
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  disabled
                />
              )}
            />
            {mtt && (
              <QuotePackageWrapper
                label="Switch BL Cost"
                unitPlaceholder="Currency"
                inputPlaceholder="Switch BL Cost"
                input={switchBlCost}
                unit={switchBlCostCurrency}
                control={control}
                unitOptions={selectedCurrency}
                currency
              />
            )}
          </div>

          <QuoteProductFields
            isEdit={isEdit}
            fields={productFields}
            handleAddProductFields={handleAddProductFields}
            handleDuplicateProductField={handleDuplicateProductField}
            remove={productRemove}
            quoteIndex={quoteIndex}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
            selectedCurrency={selectedCurrency}
            filterProductOptions={filterProductOptions}
            productOptions={productOptions}
            filterState={filterState}
            product={product}
            incoTermsWatch={incoTermsWatch}
            primaryUnitWatch={primaryUnitWatch}
            currency1Watch={currency1Watch}
            fxRateWatch={fxRateWatch}
            totalLocalFreightWatch={totalLocalFreightWatch}
            totalLocalFreightCurrencyWatch={totalLocalFreightCurrencyWatch}
            totalPortAndClearanceExpensesWatch={totalPortAndClearanceExpensesWatch}
            totalPortAndClearanceExpensesCurrencyWatch={totalPortAndClearanceExpensesCurrencyWatch}
            totalFreightWatch={totalFreightWatch}
            totalFreightCurrencyWatch={totalFreightCurrencyWatch}
            totalWeightWatch={totalWeightWatch}
            totalWeightUnitWatch={totalWeightUnitWatch}
            switchBlCostWatch={switchBlCostWatch}
            switchBlCostCurrencyWatch={switchBlCostCurrencyWatch}
            financeCostWatch={financeCostWatch}
            discountWatch={discountWatch}
            insuranceCostWatch={insuranceCostWatch}
            isEnquiry={isEnquiry}
          />

          <Divider className={css.divider} />
          <div className={css.rowWrapper}>
            <Controller
              name={financeCost}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={
                    <span>
                      Finance cost
                      <Typography variant="label"> ( % )</Typography>
                    </span>
                  }
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={
                    fieldState.error?.message ??
                    (currency1Watch?.label
                      ? getCommaSeperatedCurrencyAmount(currency1Watch?.label, totalFinanceCost)
                      : undefined)
                  }
                />
              )}
            />
            <Controller
              name={discount}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={
                    <span>
                      Discount
                      <Typography variant="label"> ( % )</Typography>
                    </span>
                  }
                  placeholder="Enter value in percentage"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default ConsolidationQuote;
